import { isValidStellarContract } from './stellar-strkey';

export type SolanaAddress = string;

/**
 * SAC for native XLM on pubnet. Kept here so the one place that needs to reject it as a
 * trackable token references a named constant rather than repeating the literal.
 */
export const STELLAR_NATIVE_XLM_CONTRACT = 'CAS3J7GYLGXMF6TDJBBYYSE3HQ6BBSMLNUQ34T6TZMYMW2EVH34XOWMA';

export const isValidSolanaAddress = (address: string): boolean => {
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
};

export const isValidSuiAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]+(::[a-zA-Z0-9_]+)+$/.test(address);
};

// Fogo uses similar address format to Solana (base58)
export const isValidFogoAddress = (address: string): boolean => {
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
};

// Stellar contract addresses ("C...") carry a CRC16 checksum, so unlike the base58 chains above
// this rejects a mistyped address without a round trip. Case-sensitive: base32 is uppercase and
// never normalized, so a lowercased address is wrong rather than equivalent.
export const isValidStellarContractAddress = (address: string): boolean => isValidStellarContract(address);

/**
 * Native XLM is a valid contract address but cannot be tracked for holders: every account holds
 * XLM without a trustline, so Horizon has no trustline list to enumerate and `?asset=native` is
 * not a valid query. Rejecting it in the form beats failing at fetch time.
 */
export const isTrackableStellarToken = (address: string): boolean =>
  isValidStellarContractAddress(address) && address !== STELLAR_NATIVE_XLM_CONTRACT;

// Sui wallet addresses are 0x + 64 hex characters (distinct from coin type addresses validated by isValidSuiAddress)
export const isValidSuiWalletAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{64}$/.test(address);
};

export const isValidNonEvmWalletAddress = (address: string): boolean => {
  return isValidSolanaAddress(address) || isValidFogoAddress(address) || isValidSuiWalletAddress(address);
};

// Hex addresses (0x-prefixed: EVM and Sui) are case-insensitive, base58 addresses (Solana/Fogo) are case-sensitive
export const isSameAddress = (a: string, b: string | undefined | null): boolean => {
  if (!b) return false;
  if (a.startsWith('0x') && b.startsWith('0x')) {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
};
